/* ============================================================================
   index.js - landing page: auth tabs, email verification, showcase marquee
   Firebase auth loads on first interaction (keeps logged-out landing fast).
   ============================================================================ */

import { validatePassword, PASSWORD_HINT } from "../password-policy.js?v=20261007m";
import { hasPreviewAccess, TEST_SIGNUP_ENABLED, checkTestSignupPassword } from "../site-gate.js?v=20261007m";
import { fillIcons, initShellEffects, ensureCopyrightFooter } from "../landing-shell.js?v=20261007m";
import { initButtonSprinkle } from "../button-sprinkle.js?v=20261007m";
import { initHomeLandingUi } from "../home-landing-ui.js?v=20261007m";
import { wirePasswordToggles } from "../password-toggle.js?v=20261007m";
import { initLandingNavActive } from "../landing-nav.js?v=20261007m";
import { renderTierCards, KOFI_URL } from "../tiers-ui.js?v=20261007m";
import { fetchMostLovedTierId, readCachedMostLovedTierId } from "../tier-most-loved.js?v=20261007m";
import { SAMPLE_PRODUCTS } from "../sample-data.js?v=20261007m";
import { paintHeroAssetCount, paintHeroAssetCountFromTotal, readCachedMarketingAssetTotal } from "../catalog-count.js?v=20261007m";
import { SHOWCASE_IMAGES } from "../showcase-images.js?v=20261007m";
import { auth as fbAuthRoot, functions, isConfigured } from "../firebase-config.js?v=20261007m";
import { httpsCallable } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-functions.js";
import { trackAuth, trackSelectContent } from "../analytics.js?v=20261007m";
import {
  accessBlockAppealHtml,
  accessBlockCopy,
  consumeAccessBlock,
  rememberAccessBlock,
} from "../access-block-copy.js?v=20261007m";
import { checkSiteAccess } from "../site-access.js?v=20261007m";
import { signOut } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js";
import { clearSignedOutCaches } from "../signed-out-caches.js?v=20261007m";
import { initNavPrefetch } from "../nav-prefetch.js?v=20261007m";

const moduleV = window.__CCJ_V || "dev";

const SHOWCASE_EAGER = 3;

function buildShowcase() {
  const track = document.getElementById("showcase-track");
  if (!track) return;

  const images = SHOWCASE_IMAGES;
  const count = images.length;
  if (!count) return;

  const loop = [...images, ...images];
  const marqueeSeconds = Math.max(30, Math.round(45 * (count / 8)));
  track.style.setProperty("--marquee-duration", `${marqueeSeconds}s`);
  track.classList.remove("is-running");
  delete track.dataset.started;

  track.innerHTML = loop
    .map((src, i) => {
      const eager = i < Math.min(count, SHOWCASE_EAGER);
      return (
        `<img class="showcase__slide" src="${encodeURI(src)}" alt="" ` +
        `loading="${eager ? "eager" : "lazy"}" decoding="async" width="256" height="176" ` +
        `data-showcase-i="${i}" />`
      );
    })
    .join("");

  const startMarquee = () => {
    if (track.dataset.started === "1") return;
    track.dataset.started = "1";
    track.classList.add("is-running");
  };

  const firstHalf = [...track.querySelectorAll("img")].slice(0, count);
  let pending = Math.min(firstHalf.length, SHOWCASE_EAGER);

  const markDone = () => {
    pending -= 1;
    if (pending <= 0) startMarquee();
  };

  firstHalf.slice(0, SHOWCASE_EAGER).forEach((img) => {
    const onFail = () => {
      const el = document.createElement("div");
      el.className = "showcase__slide showcase__fallback";
      el.setAttribute("aria-hidden", "true");
      img.replaceWith(el);
      markDone();
    };

    if (img.complete) {
      if (img.naturalWidth > 0) markDone();
      else onFail();
      return;
    }
    img.addEventListener("load", markDone, { once: true });
    img.addEventListener("error", onFail, { once: true });
  });

  if (pending <= 0) startMarquee();
  window.setTimeout(startMarquee, 5000);
}

const authPanel = document.getElementById("auth-panel");
const authVerify = document.getElementById("auth-verify");
const authForgot = document.getElementById("auth-forgot");
const form = document.getElementById("auth-form");
const tabs = document.querySelectorAll(".auth-tab");
const submit = document.getElementById("auth-submit");
const hint = document.getElementById("auth-hint");
const msg = document.getElementById("auth-msg");
const verifyMsg = document.getElementById("verify-msg");
const verifyEmail = document.getElementById("verify-email");
const passwordInput = document.getElementById("password");
const passwordError = document.getElementById("password-error");
const inviteField = document.getElementById("invite-field");
const inviteInput = document.getElementById("invite");
const forgotMsg = document.getElementById("forgot-msg");
const forgotEmailInput = document.getElementById("forgot-email");

if (!hasPreviewAccess()) {
  window.location.replace("index.html");
}

let mode = "login";
let authModulePromise = null;
let authListenersWired = false;
let enteringApp = false;

function setBootText(text) {
  const el = document.getElementById("auth-boot-text");
  if (el) el.textContent = text;
}

function revealLanding() {
  document.documentElement.classList.remove("ccj-auth-pending");
}

function showAccessBlockNotice(copy) {
  const panel = document.getElementById("access-block-notice");
  if (!panel || !copy) return;
  const title = document.getElementById("access-block-title");
  const body = document.getElementById("access-block-body");
  const appeal = document.getElementById("access-block-appeal");
  if (title) title.textContent = copy.title || "";
  if (body) body.textContent = copy.body || "";
  if (appeal) appeal.innerHTML = accessBlockAppealHtml();
  panel.hidden = false;
  try {
    panel.scrollIntoView({ block: "nearest", behavior: "smooth" });
  } catch (_) {}
}

function enterApp(uid) {
  if (enteringApp) return;
  enteringApp = true;
  document.documentElement.classList.add("ccj-redirecting", "ccj-auth-pending");
  setBootText("Opening your jar…");
  try {
    if (uid) sessionStorage.setItem("ccj_last_uid", uid);
  } catch (_) {}
  window.Turbo?.cache?.clear?.();
  window.location.replace("dashboard.html");
}

async function exitBlockedSession(reason) {
  const kind = reason === "ip" ? "ip" : "account";
  rememberAccessBlock(kind);
  try {
    clearSignedOutCaches();
    if (fbAuthRoot?.currentUser) await signOut(fbAuthRoot);
  } catch (_) {}
  enteringApp = false;
  document.documentElement.classList.remove("ccj-redirecting", "ccj-auth-pending");
  revealLanding();
  showAuthForm();
  showAccessBlockNotice(accessBlockCopy(kind));
}

async function tryEnterApp(uid) {
  if (enteringApp) return;
  enteringApp = true;
  document.documentElement.classList.add("ccj-redirecting", "ccj-auth-pending");
  setBootText("Opening your jar…");

  const result = await checkSiteAccess({ force: true });
  if (result?.ok === false) {
    await exitBlockedSession(result.reason === "ip" ? "ip" : "account");
    return;
  }

  // Admin OTP runs on admin*.html only (page-boot), not on sign-in to the vault.
  enteringApp = false;
  enterApp(uid);
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function forgotPasswordErrorMessage(err) {
  const code = err?.code || "";
  const msg = String(err?.message || "")
    .replace(/^Firebase:\s*/i, "")
    .replace(/\s*\([^)]*\)\s*\.?\s*$/, "")
    .trim();

  if (code === "functions/not-found") {
    return "No account found for that email. Try creating one instead.";
  }
  if (code === "functions/invalid-argument") {
    return msg || "That email address doesn't look right.";
  }
  if (code === "functions/failed-precondition") {
    return msg || "Password reset is unavailable right now. Try again in a moment.";
  }
  if (code.startsWith("functions/") && msg) {
    return msg;
  }
  if (code === "auth/too-many-requests") {
    return "Too many attempts. Wait a few minutes and try again.";
  }
  if (code === "auth/network-request-failed") {
    return "Network error. Check your connection and try again.";
  }
  if (err?.message && !code) return err.message;
  return "Could not send reset email. Try again in a moment.";
}

function loadAuthModule() {
  if (!authModulePromise) {
    // Keep auth off the Vite landing graph so we always hit the live /js/auth.js
    // (bundling it pulled a stale kitchen chunk and broke verify refresh).
    authModulePromise = import(/* @vite-ignore */ `/js/auth.js?v=${moduleV}`);
  }
  return authModulePromise;
}

function primeLandingAuth() {
  loadAuthModule();
}

function wireAuthListeners(auth) {
  if (authListenersWired) return;
  authListenersWired = true;

  auth.onUser((user) => {
    if (!user) {
      revealLanding();
      showAuthForm();
      return;
    }
    if (auth.needsVerification(user)) {
      revealLanding();
      showVerifyPanel(user.email);
      // Just came back from the email link, or verified in another tab.
      void (async () => {
        try {
          const verified = await auth.refreshVerification();
          if (verified) {
            await tryEnterApp(user.uid);
          }
        } catch (err) {
          console.warn("[CookieCodeJar] Auto verification refresh failed:", err);
        }
      })();
      return;
    }
    void tryEnterApp(user.uid);
  });

  document.getElementById("verify-refresh")?.addEventListener("click", async () => {
    const btn = document.getElementById("verify-refresh");
    const api = await loadAuthModule();
    btn.disabled = true;
    btn.textContent = "Checking…";
    clearVerifyMsg();

    try {
      const verified = await api.refreshVerification();
      if (verified) {
        const { auth: fbAuth } = await import(/* @vite-ignore */ `/js/firebase-config.js?v=${moduleV}`);
        await tryEnterApp(fbAuth?.currentUser?.uid);
        return;
      }
      // Firebase often keeps emailVerified=false on the old session even after
      // the link worked. Fresh sign-in picks up the server flag.
      await api.signOutUser();
      showAuthForm();
      setMode("login");
      showMsg("If you clicked the confirmation link, sign in again to enter.", "ok");
    } catch (err) {
      console.error("[CookieCodeJar] Verification refresh failed:", err);
      showVerifyMsg("Couldn't check your status. Try again in a moment.", "error");
    } finally {
      btn.disabled = false;
      btn.textContent = "I've confirmed - continue";
    }
  });

  document.getElementById("verify-resend")?.addEventListener("click", async () => {
    const btn = document.getElementById("verify-resend");
    const api = await loadAuthModule();
    btn.disabled = true;
    clearVerifyMsg();

    try {
      await api.resendVerification();
      showVerifyMsg("Another confirmation email is on its way.", "ok");
    } catch (err) {
      console.error("[CookieCodeJar] Resend verification failed:", err);
      showVerifyMsg("Couldn't resend right now. Wait a minute and try again.", "error");
    } finally {
      btn.disabled = false;
    }
  });

  document.getElementById("verify-signout")?.addEventListener("click", async () => {
    const api = await loadAuthModule();
    await api.signOutUser();
    showAuthForm();
    clearMsg();
    setMode("login");
  });
}

async function ensureAuthReady() {
  const auth = await loadAuthModule();
  wireAuthListeners(auth);
  return auth;
}

function showMsg(text, kind = "error") {
  msg.textContent = text;
  msg.className = `auth-msg is-${kind === "error" ? "error" : "ok"}`;
}
function clearMsg() {
  msg.textContent = "";
  msg.className = "auth-msg";
}
function showPasswordError(text) {
  if (!passwordError) {
    if (text) showMsg(text);
    return;
  }
  passwordError.textContent = text || "";
  passwordError.hidden = !text;
  passwordInput?.setAttribute("aria-invalid", text ? "true" : "false");
  passwordInput?.classList.toggle("is-invalid", Boolean(text));
}
function clearPasswordError() {
  showPasswordError("");
}
function showVerifyMsg(text, kind = "error") {
  if (!verifyMsg) return;
  verifyMsg.textContent = text;
  verifyMsg.className = `auth-msg is-${kind === "error" ? "error" : "ok"}`;
}
function clearVerifyMsg() {
  if (!verifyMsg) return;
  verifyMsg.textContent = "";
  verifyMsg.className = "auth-msg";
}

const authTabs = document.querySelector(".auth-card__tabs");

function showAuthForm() {
  authPanel?.removeAttribute("hidden");
  authVerify?.setAttribute("hidden", "");
  authForgot?.setAttribute("hidden", "");
  authTabs?.removeAttribute("hidden");
  clearVerifyMsg();
  clearForgotMsg();
}

function showVerifyPanel(email) {
  authPanel?.setAttribute("hidden", "");
  authForgot?.setAttribute("hidden", "");
  authVerify?.removeAttribute("hidden");
  authTabs?.setAttribute("hidden", "");
  if (verifyEmail) verifyEmail.textContent = email || "your inbox";
  clearMsg();
  clearVerifyMsg();
  fillIcons(authVerify);
}

function showForgotMsg(text, kind = "error") {
  if (!forgotMsg) return;
  forgotMsg.textContent = text;
  forgotMsg.className = `auth-msg is-${kind === "error" ? "error" : "ok"}`;
}

function clearForgotMsg() {
  if (!forgotMsg) return;
  forgotMsg.textContent = "";
  forgotMsg.className = "auth-msg";
}

function showForgotPanel(prefillEmail = "") {
  authPanel?.setAttribute("hidden", "");
  authVerify?.setAttribute("hidden", "");
  authForgot?.removeAttribute("hidden");
  authTabs?.setAttribute("hidden", "");
  clearMsg();
  clearVerifyMsg();
  clearForgotMsg();
  if (forgotEmailInput) forgotEmailInput.value = prefillEmail || "";
  fillIcons(authForgot);
  forgotEmailInput?.focus();
}

if (location.protocol === "file:") {
  showMsg(
    "Open through a local server (http://localhost:5173), not by double-clicking the HTML file."
  );
}

function setMode(next) {
  mode = next;
  tabs.forEach((t) => {
    const active = t.dataset.tab === mode;
    t.classList.toggle("is-active", active);
    t.setAttribute("aria-selected", active ? "true" : "false");
  });
  const signup = mode === "signup";
  submit.textContent = signup ? "Create account" : "Enter the jar";
  passwordInput.autocomplete = signup ? "new-password" : "current-password";
  passwordInput.placeholder = signup ? PASSWORD_HINT : "Your password";
  if (inviteField) {
    const showInvite = signup && TEST_SIGNUP_ENABLED;
    inviteField.hidden = !showInvite;
    if (inviteInput) {
      inviteInput.required = showInvite;
      if (!showInvite) inviteInput.value = "";
    }
  }
  const legalHint = document.getElementById("auth-legal-hint") || document.querySelector(".auth-legal-hint");
  const forgotTrigger = document.getElementById("auth-forgot-trigger");
  if (forgotTrigger) forgotTrigger.hidden = signup;
  if (legalHint) legalHint.hidden = !signup;
  if (hint) {
    if (signup) {
      hint.hidden = false;
      hint.textContent = TEST_SIGNUP_ENABLED
        ? "Test mode: you'll need the invite password, then confirm your email."
        : "We'll email a confirmation link before you can get in.";
    } else {
      hint.hidden = true;
      hint.textContent = "";
    }
  }
  clearMsg();
  clearPasswordError();
}

tabs.forEach((t) => {
  t.setAttribute("aria-selected", t.classList.contains("is-active") ? "true" : "false");
});
tabs.forEach((t) =>
  t.addEventListener("click", () => {
    primeLandingAuth();
    setMode(t.dataset.tab);
    trackSelectContent(t.dataset.tab === "signup" ? "auth_tab_signup" : "auth_tab_login");
  })
);

form?.addEventListener("focusin", () => primeLandingAuth(), { once: true });
authPanel?.addEventListener("focusin", () => primeLandingAuth(), { once: true });

document.getElementById("auth-forgot-trigger")?.addEventListener("click", () => {
  primeLandingAuth();
  const prefill = form?.email?.value?.trim() || "";
  showForgotPanel(prefill);
});

document.getElementById("forgot-back")?.addEventListener("click", () => {
  showAuthForm();
  setMode("login");
});

document.getElementById("forgot-submit")?.addEventListener("click", async () => {
  const btn = document.getElementById("forgot-submit");
  const email = forgotEmailInput?.value?.trim() || "";

  if (!email) {
    showForgotMsg("Enter your email address.", "error");
    return;
  }
  if (!isValidEmail(email)) {
    showForgotMsg("That email address doesn't look right.", "error");
    return;
  }
  if (!isConfigured || !functions) {
    showForgotMsg("Password reset is unavailable right now. Try again in a moment.", "error");
    return;
  }

  btn.disabled = true;
  btn.textContent = "Sending…";
  clearForgotMsg();

  try {
    await httpsCallable(functions, "sendBrandedPasswordResetEmail")({ email });
    showForgotMsg("Reset instructions sent. Check your inbox and spam.", "ok");
  } catch (err) {
    console.error("[CookieCodeJar] Password reset request failed:", err);
    showForgotMsg(forgotPasswordErrorMessage(err), "error");
  } finally {
    btn.disabled = false;
    btn.textContent = "Send reset link";
  }
});

form.addEventListener("submit", async (e) => {
  e.preventDefault();
  const auth = await ensureAuthReady();
  const email = form.email.value.trim();
  const password = form.password.value;

  if (!email || !password) return showMsg("Enter your email and password to continue.");
  clearPasswordError();
  if (mode === "signup") {
    const inviteCheck = await checkTestSignupPassword(inviteInput?.value);
    if (!inviteCheck.ok) return showMsg(inviteCheck.message);
    const check = validatePassword(password);
    if (!check.ok) return showPasswordError(check.message);
  }

  submit.disabled = true;
  submit.textContent = mode === "signup" ? "Creating…" : "Entering…";

  try {
    if (mode === "signup") {
      await auth.signUp(email, password);
      trackAuth("signup");
      revealLanding();
      showVerifyPanel(email);
      showVerifyMsg("Confirmation email sent. Check your inbox (and spam).", "ok");
    } else {
      const user = await auth.signIn(email, password);
      trackAuth("login");
      if (auth.needsVerification(user)) {
        revealLanding();
        showVerifyPanel(user.email);
        showVerifyMsg("Confirm your email before entering the vault.", "error");
      } else {
        await tryEnterApp(user.uid);
        return;
      }
    }
  } catch (err) {
    console.error("[CookieCodeJar] Auth error:", err);
    revealLanding();
    if (String(err?.code || "").includes("user-disabled")) {
      showAccessBlockNotice(accessBlockCopy("account"));
      clearMsg();
      return;
    }
    showMsg(friendlyError(err));
    if (isPasswordAuthError(err)) {
      clearMsg();
      showPasswordError(passwordAuthError(err));
    } else {
      clearPasswordError();
    }
  } finally {
    if (!enteringApp) {
      submit.disabled = false;
      submit.textContent = mode === "signup" ? "Create account" : "Enter the jar";
    }
  }
});

function isPasswordAuthError(err) {
  const code = String(err?.code || "");
  return (
    code.includes("invalid-credential") ||
    code.includes("wrong-password") ||
    code.includes("weak-password")
  );
}

function passwordAuthError(err) {
  const code = String(err?.code || "");
  if (code.includes("weak-password")) {
    return "Choose a stronger password (8+ chars, capital letter, number or symbol).";
  }
  return "Password didn't match. Check it and try again.";
}

function friendlyError(err) {
  const code = err?.code || "";
  if (code.includes("user-disabled"))
    return "This account is blocked for safety reasons. Message me on Instagram or Ko-fi to appeal.";
  if (code.includes("email-already-in-use")) return "That email already has an account. Try signing in.";
  if (code.includes("invalid-email")) return "That email address doesn't look right.";
  if (code.includes("invalid-credential") || code.includes("wrong-password"))
    return "Email or password didn't match. Try again.";
  if (code.includes("user-not-found")) return "No account with that email. Create one instead.";
  if (code.includes("weak-password")) return "Choose a stronger password (8+ chars, capital letter, number or symbol).";
  if (code.includes("operation-not-allowed"))
    return "Email/Password sign-in isn't enabled yet in your Firebase console.";
  if (code.includes("network-request-failed")) return "Network error. Check your connection and try again.";
  if (code.includes("permission-denied") || code.includes("insufficient"))
    return "Account made, but Firestore blocked saving your profile. Create Firestore and check security rules.";
  if (code.includes("unavailable") || code.includes("not-found"))
    return "Firestore isn't set up yet. In Firebase, go to Databases & Storage → Firestore → Create database.";
  if (code.includes("too-many-requests"))
    return "Too many attempts. Wait a few minutes before requesting another email.";
  return "Something went wrong. Please try again.";
}

const isCoverLanding = document.body.classList.contains("landing--cover");
if (isCoverLanding) {
  initButtonSprinkle();
  initHomeLandingUi();
} else {
  initShellEffects();
}
fillIcons(document);
import(`../analytics.js?v=${moduleV}`)
  .then((mod) => mod.bootAnalytics())
  .catch(() => {});
if (document.getElementById("showcase-track")) buildShowcase();
const cachedAssetTotal = readCachedMarketingAssetTotal();
if (cachedAssetTotal != null) paintHeroAssetCountFromTotal(cachedAssetTotal);
else paintHeroAssetCount(SAMPLE_PRODUCTS);
renderTierCards(document.getElementById("landing-tier-grid"), null, {
  featuredTierId: readCachedMostLovedTierId(),
});
void fetchMostLovedTierId().then((featuredTierId) => {
  renderTierCards(document.getElementById("landing-tier-grid"), null, { featuredTierId });
});
const landingKofi = document.getElementById("landing-kofi-link");
if (landingKofi) landingKofi.href = KOFI_URL;
if (document.querySelector(".landing-nav__links")) initLandingNavActive();
ensureCopyrightFooter();
wirePasswordToggles(document.getElementById("auth-form") || document);

passwordInput?.addEventListener("input", () => clearPasswordError());

const pendingBlock = consumeAccessBlock();
if (pendingBlock) showAccessBlockNotice(pendingBlock);

function focusLandingAuth() {
  primeLandingAuth();
  document.getElementById("email")?.focus();
}
document.querySelectorAll('a[href="#join"]').forEach((a) => {
  a.addEventListener("click", () => {
    requestAnimationFrame(focusLandingAuth);
  });
});
if (location.hash === "#join") focusLandingAuth();

try {
  const launchParams = new URLSearchParams(location.search);
  if (launchParams.get("verified") === "1") {
    showAuthForm();
    setMode("login");
    showMsg("Email confirmed. Sign in to enter the jar.", "ok");
    history.replaceState({}, "", location.pathname + location.hash);
  }
} catch (_) {}

const authBootDeadline = window.setTimeout(() => {
  revealLanding();
  setBootText("Sign in below when you're ready.");
}, 12000);

ensureAuthReady()
  .catch((err) => {
    console.error("[CookieCodeJar] Auth boot failed:", err);
    revealLanding();
    showMsg("Having trouble reaching the jar. You can still try signing in.");
  })
  .finally(() => window.clearTimeout(authBootDeadline));

initNavPrefetch();
